/**
 * Main Application Router
 *
 * This component defines all the application routes and serves as the
 * main layout wrapper for authenticated pages.
 *
 * Page Structure:
 * - Spinner: Global loading indicator
 * - Header: Top navigation bar with user info and logout
 * - DialogsProvider: Global modal/dialog context
 * - PageNavigation: Side navigation menu
 * - Routes: Page content based on current URL
 *
 * Available Routes:
 * - /products              - Product management (default landing page)
 * - /jobs                  - Job processing and status
 * - /dateMath              - Date math calculations
 * - /uploadTemplates       - Bulk upload templates
 * - /rightslineProductGate - Rightsline product gate management
 * - /dealVersion           - Deal version tracking
 * - /rights                - Rights management
 * - /pmxDeliveries         - PMX deliveries
 * - /users                 - User administration
 * - /deal-dro-change       - Deal DRO changes
 *
 * Adding a New Page:
 * 1. Create page component in src/datastage/pages/YourPage/
 * 2. Import the page component here
 * 3. Add a new <Route> with the path and element
 * 4. Add navigation link in PageNavigation component
 *
 * @see src/datastage/components/PageNavigation - Side navigation menu
 * @see src/datastage/components/Header - Top navigation bar
 */
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { initialize } from './store/auth';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Header } from './components/Header';
import { Spinner } from './components/Spinner';
import { PageNavigation } from './components/PageNavigation';
import { UploadTemplatesPage } from './pages/UploadTemplates';
import { JobsPage } from './pages/Jobs';
import { DateMathPage } from './pages/DateMath';
import { DealVersionPage } from './pages/DealVersion';
import { RightslineProductGatePage } from './pages/RightslineProductGate';
import { RightsPage } from './pages/Rights';
import { PMXDeliveriesPage } from './pages/PMXDeliveries';
import { ProductsPage } from './pages/Products';
import { DialogsProvider } from './components/Dialogs';
import { UsersPage } from './pages/Users';
import { DealDROChangesPage } from './pages/DealDROChanges';
import { usePermissions } from './utils/permissions';

/**
 * Protected route component that checks user permissions.
 * Redirects to /products if user lacks required permission.
 */
function ProtectedRoute({ children, hasPermission }) {
    if (!hasPermission) {
        return <Navigate to="/products" replace />;
    }
    return children;
}

export default function DatastageRouter() {
    const dispatch = useDispatch();
    const {
        isAdmin,
        isRLProductGateUser,
        isRLProductGateEspnPlusUser,
        isJobTrackerUser,
        isBulkDateMathToolUser,
        isRLImportTemplateUser,
    } = usePermissions();

    // Initialize application state on mount (loads user data, etc.)
    useEffect(() => {
        dispatch(initialize());
    }, [dispatch]);

    return (
        <>
            <Spinner />
            <Header />
            <DialogsProvider>
                <PageNavigation>
                    <Routes>
                        <Route
                            path="/uploadTemplates"
                            element={
                                <ProtectedRoute hasPermission={isRLImportTemplateUser}>
                                    <UploadTemplatesPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/products" element={<ProductsPage />} />
                        <Route
                            path="/jobs"
                            element={
                                <ProtectedRoute hasPermission={isJobTrackerUser}>
                                    <JobsPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dateMath"
                            element={
                                <ProtectedRoute hasPermission={isBulkDateMathToolUser}>
                                    <DateMathPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/rightslineProductGate"
                            element={
                                <ProtectedRoute hasPermission={isAdmin || isRLProductGateUser || isRLProductGateEspnPlusUser}>
                                    <RightslineProductGatePage />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/dealVersion" element={<DealVersionPage />} />
                        <Route path="/rights" element={<RightsPage />} />
                        <Route path="/pmxDeliveries" element={<PMXDeliveriesPage />} />
                        <Route
                            path="/users"
                            element={
                                <ProtectedRoute hasPermission={isAdmin}>
                                    <UsersPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/deal-dro-change" element={<DealDROChangesPage />} />
                    </Routes>
                </PageNavigation>
            </DialogsProvider>
        </>
    );
}
